/*
 * Character reference review — the three calls behind the review panel.
 *
 *   GET  /videos/{id}/references                               the current review
 *   POST /videos/{id}/references/characters/{key}/regenerate   one character, with notes
 *   POST /videos/{id}/references/approve                       the exact versions seen
 *
 * Wire shapes mirror valoi_engine's `app/datatypes.py` (the Reference* models at the
 * end of the file). They are mapped to camelCase here so nothing past this file knows
 * the wire spelling.
 *
 * Uploading a replacement photo is a deliberate v1 descope, not a missing field: the
 * model below leaves room for it (a version is "the images of one character at one
 * point"), but there is no upload call, control or copy until it is approved.
 */
import type { ProcessingStatus } from '@/shared/types';
import { getJson, postJson, resolveApiUrl } from '@/shared/api/apiClient';
import { normalizeStatus } from '@/shared/api/backendContract';
import { DEFAULT_MAX_NOTES_CHARS } from '@/features/references/utils/referenceNotes';

export type ReferenceAngle = 'front' | 'back' | 'side';

/** Every state an operation can be in, as the engine reports it. */
export type ReferenceOpState =
  | 'reserving'
  | 'queued'
  | 'running'
  | 'cancelling'
  | 'succeeded'
  | 'failed'
  | 'needs_attention'
  | 'rejected'
  | 'discarded';

/** States in which work is still going to happen — the ones worth polling for. */
const LIVE_OP_STATES: readonly string[] = ['reserving', 'queued', 'running', 'cancelling'];

export function isLiveOp(op: { state: string } | null | undefined): boolean {
  return Boolean(op && LIVE_OP_STATES.includes(op.state));
}

export type ReferenceOp = {
  id: string;
  state: ReferenceOpState;
  clientRequestId: string;
  notes?: string;
  error?: string;
  /** When the op last changed state (ISO). */
  updatedAt?: string;
};

export type ReferenceImage = { angle: ReferenceAngle; url?: string };

export type ReferenceCharacter = {
  charKey: string;
  /** A readable label from the story plan; absent when the plan had none. */
  name?: string;
  version: number;
  images: ReferenceImage[];
  effectiveDescription?: string;
  /** The notes the selected version was generated with — pre-fills the notes box. */
  notes?: string;
  regenerationsUsed: number;
  /** Left under the server's cap; null when no cap is enforced (the default). */
  regenerationsRemaining: number | null;
  activeOp: ReferenceOp | null;
  lastOp: ReferenceOp | null;
};

export type ReferenceReview = {
  jobId: string;
  status: ProcessingStatus;
  canApprove: boolean;
  approved: boolean;
  /** When the review lapses if nobody touches it; absent once it is not in review. */
  expiresAt?: string;
  regenerationsRemainingForJob: number | null;
  /** The longest replacement-notes block the server accepts. */
  maxNotesChars: number;
  characters: ReferenceCharacter[];
};

type OpApi = {
  id: string;
  state: ReferenceOpState;
  client_request_id: string;
  notes?: string | null;
  error?: string | null;
  updated_at?: string | null;
};

type ReviewApi = {
  job_id: string;
  status: string;
  can_approve: boolean;
  approved: boolean;
  expires_at?: string | null;
  regenerations_remaining_for_job?: number | null;
  max_notes_chars?: number | null;
  characters: {
    char_key: string;
    name?: string | null;
    version: number;
    images: { angle: ReferenceAngle; url?: string | null }[];
    effective_description?: string | null;
    notes?: string | null;
    regenerations_used: number;
    regenerations_remaining?: number | null;
    active_op?: OpApi | null;
    last_op?: OpApi | null;
  }[];
};

const mapOp = (op: OpApi | null | undefined): ReferenceOp | null =>
  op
    ? {
        id: op.id,
        state: op.state,
        clientRequestId: op.client_request_id,
        notes: op.notes ?? undefined,
        error: op.error ?? undefined,
        updatedAt: op.updated_at ?? undefined,
      }
    : null;

const base = (jobId: string) => `/api/v1/videos/${encodeURIComponent(jobId)}/references`;

export async function fetchReferenceReview(jobId: string): Promise<ReferenceReview> {
  const r = await getJson<ReviewApi>(base(jobId));
  return {
    jobId: r.job_id,
    status: normalizeStatus(r.status),
    canApprove: r.can_approve,
    approved: r.approved,
    expiresAt: r.expires_at ?? undefined,
    regenerationsRemainingForJob: r.regenerations_remaining_for_job ?? null,
    maxNotesChars: r.max_notes_chars ?? DEFAULT_MAX_NOTES_CHARS,
    characters: r.characters.map((c) => ({
      charKey: c.char_key,
      name: c.name ?? undefined,
      version: c.version,
      images: c.images.map((i) => ({ angle: i.angle, url: resolveApiUrl(i.url) })),
      effectiveDescription: c.effective_description ?? undefined,
      notes: c.notes ?? undefined,
      regenerationsUsed: c.regenerations_used,
      regenerationsRemaining: c.regenerations_remaining ?? null,
      activeOp: mapOp(c.active_op),
      lastOp: mapOp(c.last_op),
    })),
  };
}

/**
 * Starts (or, with a repeated `clientRequestId`, re-reads) one regeneration.
 *
 * The id is the whole idempotency story: the engine returns the original operation
 * for a repeat of the same id instead of reserving credits and generating again. The
 * caller therefore generates one per click and reuses it only for a retry of THAT
 * click (see useReferenceReview) — never across two deliberate regenerations.
 */
export async function regenerateCharacter(
  jobId: string,
  charKey: string,
  notes: string,
  clientRequestId: string,
): Promise<ReferenceOp> {
  const r = await postJson<{ op: OpApi }>(
    `${base(jobId)}/characters/${encodeURIComponent(charKey)}/regenerate`,
    { notes, client_request_id: clientRequestId },
  );
  return mapOp(r.op) as ReferenceOp;
}

/** Approves exactly the versions the user was looking at: `{ charKey: version }`. */
export async function approveReferences(
  jobId: string,
  reviewedVersions: Record<string, number>,
): Promise<{ jobId: string; status: ProcessingStatus }> {
  const r = await postJson<{ job_id: string; status: string }>(`${base(jobId)}/approve`, {
    reviewed_versions: reviewedVersions,
  });
  return { jobId: r.job_id ?? jobId, status: normalizeStatus(r.status) };
}

/** One id per click. `crypto.randomUUID` needs a secure context; fall back for http dev. */
export function newClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
